// @ts-nocheck
import React, { useEffect } from "react";
import { useParams, useNavigate } from "@tanstack/react-router";
import { useForm, useStore } from "@tanstack/react-form";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { trpcClient } from "@/lib/trpc";
import { plateFormSchema, type PlateFormData } from "@/shared/schemas/tech.schema";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ArrowLeft, Save, AlertTriangle, MapPin, Layers, Wrench, Building2 } from "lucide-react";
import { toast } from "sonner";
import { focusFirstError } from "@/lib/form/formUtils";
import { sanitizeNullsDeep } from "@/lib/form/sanitize";
import { TanstackFieldWrapper, TanstackFormControl } from "@/components/ui/tanstack-form";
import { ComboboxConfigMaster } from "@/components/shared/ComboboxConfigMaster";
import { SharedCombobox } from "@/components/shared/SharedCombobox";
import StandardDetailLayout from "@/layouts/StandardDetailLayout";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";

export interface PlateFormProps {
  id?: string;
  defaultValues?: Partial<PlateFormData>;
  onSuccess?: (newItem: { id: string; label: string; original?: any }) => void;
  onCancel?: () => void;
  isModal?: boolean;
}

export function PlateForm(props?: PlateFormProps) {
  const routeParams = (useParams({ strict: false }) as any) || {};
  const isModal = props?.isModal ?? false;
  // Trang sửa dùng tham số /plates/$plateId/edit; form mở dạng modal không lấy mã từ trang đang đứng
  const id = props?.id || (isModal ? undefined : routeParams.plateId || routeParams.id);
  const isEdit = !!id && id !== "new";
  const navigate = useNavigate();
  const queryClient = useQueryClient();

  const { data: initialData, isLoading } = useQuery({
    queryKey: ["plate", id],
    queryFn: () => trpcClient.technical.plates.getById.query({ id }),
    enabled: isEdit,
  });

  const createMutation = useMutation({
    mutationFn: (data: PlateFormData) => trpcClient.technical.plates.create.mutate(data),
    onSuccess: (result: any) => {
      toast.success("Đã tạo bản in thành công!");
      queryClient.invalidateQueries({ queryKey: ["plates"] });
      queryClient.invalidateQueries({ queryKey: ["combobox"] });
      if (props?.onSuccess) {
        props.onSuccess({
          id: result?.id,
          label: result?.name || result?.plateId || "",
          original: result,
        });
      } else {
        navigate({ to: "/plates" });
      }
    },
    onError: (err: any) => toast.error("Lỗi: " + err.message),
  });

  const updateMutation = useMutation({
    mutationFn: (data: PlateFormData) => trpcClient.technical.plates.update.mutate({ id, data }),
    onSuccess: (result: any) => {
      toast.success("Đã cập nhật bản in thành công!");
      queryClient.invalidateQueries({ queryKey: ["plates"] });
      queryClient.invalidateQueries({ queryKey: ["plate", id] });
      queryClient.invalidateQueries({ queryKey: ["combobox"] });
      if (props?.onSuccess) {
        props.onSuccess({
          id: result?.id || id,
          label: result?.name || "",
          original: result,
        });
      } else {
        navigate({ to: "/plates" });
      }
    },
    onError: (err: any) => toast.error("Lỗi: " + err.message),
  });

  const form = useForm({
    defaultValues: {
      name: props?.defaultValues?.name || "",
      productName: props?.defaultValues?.productName || "",
      productId: props?.defaultValues?.productId || "",
      customerId: props?.defaultValues?.customerId || "",
      size: props?.defaultValues?.size || "",
      colors: props?.defaultValues?.colors || [],
      designFileUrl: props?.defaultValues?.designFileUrl || "",
      ctpFileUrl: props?.defaultValues?.ctpFileUrl || "",
      location: props?.defaultValues?.location || "",
      storageZone: props?.defaultValues?.storageZone || "",
      storageRack: props?.defaultValues?.storageRack || "",
      storageShelf: props?.defaultValues?.storageShelf || "",
      status: props?.defaultValues?.status || "AVAILABLE",
      usageCount: (props?.defaultValues as any)?.usageCount || 0,
      maxImpressions: (props?.defaultValues as any)?.maxImpressions || 40000,
      version: props?.defaultValues?.version || 1,
      expectedDate: props?.defaultValues?.expectedDate || "",
      vendorId: props?.defaultValues?.vendorId || "",
      outsourceCost: (props?.defaultValues as any)?.outsourceCost || ("" as any),
    } as any,
    validators: {
      onChange: plateFormSchema,
    },
    onSubmit: async ({ value }) => {
      if (duplicateErrors.length > 0) {
        toast.error("Vui lòng sửa lỗi trùng lặp dữ liệu trước khi lưu.");
        return;
      }
      if (isEdit) {
        await updateMutation.mutateAsync(value);
      } else {
        await createMutation.mutateAsync(value);
      }
    },
  });

  const { data: existingPlates = [] } = useQuery({
    queryKey: ["plates-all-check"],
    queryFn: () => trpcClient.technical.plates.list.query({ limit: 1000 }).then((res) => res.items as any[]),
  });

  const nameValue = useStore(form.store, (s: any) => s.values.name);

  const duplicateErrors = React.useMemo(() => {
    if (!existingPlates.length) return [];
    if (!nameValue) return [];
    
    const nInput = nameValue.toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
    
    return existingPlates.filter((c: any) => {
      // trang sửa mở theo mã: so với id thật của bản ghi đang sửa
      if (isEdit && (c.id === id || c.id === initialData?.id)) return false;
      const cName = (c.name || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").trim();
      return cName === nInput;
    });
  }, [existingPlates, nameValue, isEdit, id, initialData?.id]);

  useEffect(() => {
    if (initialData && isEdit) {
      const safeData = sanitizeNullsDeep(initialData);
      Object.keys(safeData).forEach((key) => {
        // trường trống trong DB giữ giá trị mặc định của form (không đổi ô nhập sang undefined)
        if (key in form.state.values && (safeData as any)[key] !== undefined) {
          form.setFieldValue(key as any, (safeData as any)[key]);
        }
      });
    }
  }, [initialData, isEdit]);

  if (isEdit && isLoading) return <div className="p-8 text-center text-slate-500">Đang tải dữ liệu...</div>;

  const formContent = (
    <div className="bg-white border rounded-lg shadow-sm p-5 space-y-6">

        {duplicateErrors.length > 0 && (
          <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-start gap-2">
            <AlertTriangle className="h-4 w-4 text-red-600 mt-0.5 shrink-0" />
            <div className="text-sm">
              <p className="font-semibold text-red-800">Lỗi: Trùng lặp dữ liệu!</p>
              <ul className="mt-1 space-y-0.5 text-red-700">
                {duplicateErrors.map((c: any) => (
                  <li key={c.id}>• {c.code || c.id.substring(0, 8)} — {c.name}</li>
                ))}
              </ul>
            </div>
          </div>
        )}


        <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
          <div className="col-span-1 md:col-span-2">
            <form.Field
              name="name"
              children={(field) => (
                <TanstackFieldWrapper field={field as any} label="Tên bản in" required>
                  <TanstackFormControl>
                    <Input className="h-9" value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} onBlur={field.handleBlur} />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            />
          </div>
          <div className="col-span-1 md:col-span-1">
            <form.Field
              name="size"
              children={(field) => (
                <TanstackFieldWrapper field={field as any} label="Kích thước (Khổ in)">
                  <TanstackFormControl>
                    <Input className="h-9" placeholder="VD: 79x109" value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} onBlur={field.handleBlur} />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            />
          </div>
          <div className="col-span-1 md:col-span-1">
            <form.Field
              name="status"
              children={(field) => (
                <TanstackFieldWrapper field={field as any} label="Trạng thái">
                  <TanstackFormControl>
                    <ComboboxConfigMaster
                      categoryCode="tooling_status"
                      value={field.state.value || "AVAILABLE"}
                      onChange={(val) => field.handleChange(val)}
                      placeholder="Chọn trạng thái..."
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            />
          </div>

          <div className="col-span-1 md:col-span-2">
            <form.Field
              name="customerId"
              children={(field) => (
                <TanstackFieldWrapper field={field as any} label="Khách hàng">
                  <TanstackFormControl>
                    <SharedCombobox type="customer" value={field.state.value || ""} onChange={(id) => {
                      field.handleChange(id);
                      form.setFieldValue("productId", "");
                    }} />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            />
          </div>
          <div className="col-span-1 md:col-span-2">
            <form.Field
              name="productId"
              children={(field) => (
                <TanstackFieldWrapper field={field as any} label="Sản phẩm áp dụng">
                  <TanstackFormControl>
                    <SharedCombobox
                      type="product"
                      value={field.state.value || ""}
                      params={
                        form.state.values.customerId
                          ? { customerId: form.state.values.customerId }
                          : undefined
                      }
                      onChange={(id, opt) => {
                        field.handleChange(id);
                        if (opt?.label) {
                          form.setFieldValue("productName", opt.label);
                        }
                      }}
                    />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            />
          </div>

          <div className="col-span-1 md:col-span-2">
            <form.Field
              name="designFileUrl"
              children={(field) => (
                <TanstackFieldWrapper field={field as any} label="Link file thiết kế">
                  <TanstackFormControl>
                    <Input className="h-9" value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} onBlur={field.handleBlur} />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            />
          </div>
          <div className="col-span-1 md:col-span-2">
            <form.Field
              name="ctpFileUrl"
              children={(field) => (
                <TanstackFieldWrapper field={field as any} label="Link file CTP">
                  <TanstackFormControl>
                    <Input className="h-9" value={field.state.value} onChange={(e) => field.handleChange(e.target.value)} onBlur={field.handleBlur} />
                  </TanstackFormControl>
                </TanstackFieldWrapper>
              )}
            />
          </div>

          {/* Section: Tọa Độ Lưu Kho Kệ Kẽm (12-Col Grid) */}
          <div className="col-span-1 md:col-span-4 rounded-xl border border-slate-200 bg-slate-50/70 p-4 space-y-3">
            <div className="flex items-center justify-between border-b border-slate-200/80 pb-2">
              <div className="flex items-center gap-2">
                <MapPin className="w-4 h-4 text-indigo-600" />
                <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                  Vị Trí Lưu Kho & Tọa Độ Kệ Kẽm (Zone • Rack • Shelf)
                </span>
              </div>
              <span className="text-[11px] text-slate-500">
                Địa chỉ lưu trữ bản in kẽm tại phòng CTP / kho kẽm
              </span>
            </div>

            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-12 sm:col-span-4">
                <form.Field
                  name="storageZone"
                  children={(field) => (
                    <TanstackFieldWrapper field={field as any} label="Khu/Dãy Kho Kẽm (Zone)">
                      <TanstackFormControl>
                        <Input 
                          className="h-9 bg-white" 
                          placeholder="VD: KHO_KEM, PHONG_CTP" 
                          value={field.state.value || ""} 
                          onChange={(e) => field.handleChange(e.target.value)} 
                          onBlur={field.handleBlur} 
                        />
                      </TanstackFormControl>
                    </TanstackFieldWrapper>
                  )}
                />
              </div>

              <div className="col-span-12 sm:col-span-4">
                <form.Field
                  name="storageRack"
                  children={(field) => (
                    <TanstackFieldWrapper field={field as any} label="Kệ/Giá (Rack)">
                      <TanstackFormControl>
                        <Input 
                          className="h-9 bg-white" 
                          placeholder="VD: K01, GIA_KEM_A" 
                          value={field.state.value || ""} 
                          onChange={(e) => field.handleChange(e.target.value)} 
                          onBlur={field.handleBlur} 
                        />
                      </TanstackFormControl>
                    </TanstackFieldWrapper>
                  )}
                />
              </div>

              <div className="col-span-12 sm:col-span-4">
                <form.Field
                  name="storageShelf"
                  children={(field) => (
                    <TanstackFieldWrapper field={field as any} label="Tầng (Shelf)">
                      <TanstackFormControl>
                        <Input 
                          className="h-9 bg-white" 
                          placeholder="VD: T1, TANG_3" 
                          value={field.state.value || ""} 
                          onChange={(e) => field.handleChange(e.target.value)} 
                          onBlur={field.handleBlur} 
                        />
                      </TanstackFormControl>
                    </TanstackFieldWrapper>
                  )}
                />
              </div>

              <div className="col-span-12">
                <form.Field
                  name="location"
                  children={(field) => (
                    <TanstackFieldWrapper field={field as any} label="Chuỗi Vị Trí Tổng Hợp">
                      <TanstackFormControl>
                        <Input 
                          className="h-9 bg-white font-mono text-xs font-semibold text-slate-800" 
                          placeholder="VD: KHO_KEM • Kệ K01 • Tầng T1" 
                          value={field.state.value || ""} 
                          onChange={(e) => field.handleChange(e.target.value)} 
                          onBlur={field.handleBlur} 
                        />
                      </TanstackFormControl>
                    </TanstackFieldWrapper>
                  )}
                />
              </div>
            </div>
          </div>

          {/* Section: Tuổi Thọ Bản In Kẽm & Đơn Vị Xuất Kẽm CTP */}
          <div className="col-span-1 md:col-span-4 rounded-xl border border-slate-200 bg-slate-50/70 p-4 space-y-3">
            <div className="flex items-center justify-between border-b border-slate-200/80 pb-2">
              <div className="flex items-center gap-2">
                <Layers className="w-4 h-4 text-emerald-600" />
                <span className="text-xs font-bold text-slate-800 uppercase tracking-wider">
                  Tuổi Thọ Bản In CTP & Đơn Vị Xuất Kẽm
                </span>
              </div>
              <span className="text-[11px] text-slate-500">
                Định mức lượt in & quản lý chi phí ra kẽm ngoài
              </span>
            </div>

            <div className="grid grid-cols-12 gap-3">
              <div className="col-span-12 sm:col-span-4">
                <form.Field
                  name="maxImpressions"
                  children={(field) => (
                    <TanstackFieldWrapper field={field as any} label="Định Mức Lượt In Tối Đa">
                      <TanstackFormControl>
                        <Input 
                          className="h-9 bg-white font-mono" 
                          type="number"
                          placeholder="40,000" 
                          value={field.state.value as string} 
                          onChange={(e) => field.handleChange(Number(e.target.value) || 0)} 
                          onBlur={field.handleBlur} 
                        />
                      </TanstackFormControl>
                    </TanstackFieldWrapper>
                  )}
                />
              </div>

              {/* Đơn vị ra kẽm / chi phí ra kẽm: chưa có chỗ lưu nên tạm ẩn (02/11/2026) */}
            </div>
          </div>
        </div>
      </div>
  );

  if (isModal) {
    return (
      <div className="space-y-4">
        {formContent}
        <div className="flex justify-end space-x-2 pt-2">
          <Button 
            variant="outline" 
            type="button"
            onClick={() => (props?.onCancel ? props.onCancel() : navigate({ to: "/plates" }))}
          >
            Hủy
          </Button>
          <Button 
            disabled={createMutation.isPending || updateMutation.isPending} 
            onClick={() => {
              form.handleSubmit();
              requestAnimationFrame(() => focusFirstError(form.state as any));
            }}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            <Save className="w-4 h-4 mr-2" />
            {isEdit ? "Cập nhật" : "Tạo mới"}
          </Button>
        </div>
      </div>
    );
  }

  return (
    <StandardDetailLayout
      breadcrumbs={[
        { label: "Bản In & Kẽm CTP", to: "/plates" },
        { label: isEdit ? (initialData?.name || "Chỉnh sửa") : "Tạo mới" },
      ]}
      title={isEdit ? `Sửa Bản In ${(initialData as any)?.plateId || id}` : "Thêm Mới Bản In"}
      onBack={() => (props?.onCancel ? props.onCancel() : navigate({ to: "/plates" }))}
      showContextPanel={false}
      actions={
        <div className="flex space-x-2">
          <Button 
            variant="outline" 
            size="sm" 
            type="button"
            onClick={() => (props?.onCancel ? props.onCancel() : navigate({ to: "/plates" }))}
          >
            Hủy
          </Button>
          <Button 
            size="sm"
            disabled={createMutation.isPending || updateMutation.isPending} 
            onClick={() => {
              form.handleSubmit();
              requestAnimationFrame(() => focusFirstError(form.state as any));
            }}
            className="bg-indigo-600 hover:bg-indigo-700 text-white"
          >
            <Save className="w-4 h-4 mr-1.5" />
            {isEdit ? "Cập nhật" : "Tạo mới"}
          </Button>
        </div>
      }
    >
      <div className="container mx-auto max-w-5xl p-4 md:p-6 space-y-6">
        {formContent}
      </div>
    </StandardDetailLayout>
  );
}

export default PlateForm;
